<template lang="pug">
Button(
    id="button-copy"
    view="secondary"
    :isDisabled="isLoading || isComplete"
    :icon="(isLoading || isComplete) ? undefined : 'icon-copy_outline'"
    @click="copy"
) {{ text }}
    
</template>

<script lang="ts">
import Vue, { computed, onUnmounted, ref, watch } from 'vue'

import { canBufferImages } from '@/packages/can-buffer-images'
import { delay } from '@/packages/utils/debug'
import { sendAppEventWithRetry } from '@/packages/namba-metrics'

import { QrForDownload } from '@/widgets/QrForDownload'

import { Button } from '@/shared/ui/Button'
import { useIsOnlyQrLayout } from '@/shared/lib/hooks/useIsOnlyQrLayout'
import { useAppStore } from '@/shared/model'

import { getQrImage } from '@/feature/get-qr-image'

function fallbackDownload(file: File): void {
    const url = URL.createObjectURL(file)

    const a = document.createElement('a')
    a.href = url
    a.download = 'qr.png'
    a.style.display = 'none'

    document.body.appendChild(a)
    a.click()

    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

function copyToClipboard(text: string) {
    // Создаем временный текстовый элемент
    const textarea = document.createElement('textarea')
    textarea.value = text

    // Скрываем элемент, чтобы он не отображался на странице
    textarea.style.position = 'fixed'
    textarea.style.left = '-99999px'
    textarea.style.fontSize = '16px'
    textarea.setAttribute('readonly', 'true')
    textarea.style.opacity = '0'

    const button = document.getElementById('button-copy')
    button?.appendChild(textarea)

    // Выделяем текст в элементе
    textarea.focus()
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length) // Для мобильных устройств

    try {
        document.execCommand('copy')
    } catch (err) {
        console.error('Ошибка при копировании:', err)
    }

    // Удаляем временный элемент
    button?.removeChild(textarea)
}

let cacheBlob: Maybe<Blob> = null
let cacheFile: Maybe<File> = null
let interval: ReturnType<typeof setInterval>

export default Vue.extend({
    name: 'ButtonCopyQrImage',
    setup() {
        const { isOnlyQrLayout } = useIsOnlyQrLayout()
        const {
            state: { url },
        } = useAppStore()

        const isLoading = ref(false)
        const isComplete = ref(false)

        const dotCount = ref(0)

        watch(isLoading, loading => {
            if (loading) {
                dotCount.value = 1
                interval = setInterval(() => {
                    dotCount.value = (dotCount.value % 3) + 1
                }, 400)
            } else {
                dotCount.value = 0
                clearInterval(interval)
            }
        })

        const text = computed(() => {
            if (isComplete.value) {
                return 'Скопировано'
            }
            if (isLoading.value) {
                return 'Копируем' + '.'.repeat(dotCount.value)
            }
            return isOnlyQrLayout.value ? 'Скопировать QR-код' : 'Скопировать'
        })

        async function copy() {
            copyToClipboard(url)

            try {
                let file: Maybe<File> = cacheFile
                let blob: Maybe<Blob> = cacheBlob

                await sendAppEventWithRetry({
                    name: 'qrCodeButtonCopyClick',
                })
                if (!file && !blob) {
                    isLoading.value = true
                    const { file: newFile, blob: newBlob } = await getQrImage()
                    file = newFile
                    blob = newBlob
                    cacheBlob = blob
                    cacheFile = file
                }

                if (blob && file) {
                    if (await canBufferImages()) {
                        await navigator.clipboard.write([
                            new ClipboardItem({
                                [blob.type]: blob,
                            }),
                        ])
                    } else {
                        if (isOnlyQrLayout.value) {
                            fallbackDownload(file)
                        }
                    }
                    isComplete.value = true
                    await delay(1000)
                    isComplete.value = false
                }
            } catch (err) {
                console.log(err)
            } finally {
                isLoading.value = false
            }
        }

        onUnmounted(() => clearInterval(interval))

        return {
            copy,
            text,
            isComplete,
            isLoading,
        }
    },
    components: {
        QrForDownload,
        Button,
    },
})
</script>
